import React from 'react';
import { Plus, Trash2, CheckCircle2, MessageSquare } from 'lucide-react';

/**
 * The team-setup chats, newest first, the way a chat app lists its history:
 * every chat is kept on the server, so a tab closed by mistake opens again
 * right where it was. A chat that made a team stays here too.
 */

const DAY = 24 * 60 * 60 * 1000;

const groupOf = (when) => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const t = new Date(when).getTime();
  if (t >= start.getTime()) return 'Today';
  if (t >= start.getTime() - DAY) return 'Yesterday';
  if (t >= start.getTime() - 7 * DAY) return 'Previous 7 days';
  return 'Older';
};

const GROUPS = ['Today', 'Yesterday', 'Previous 7 days', 'Older'];

const ChatHistory = ({ chats, currentId, onNew, onPick, onDelete, className = '' }) => {
  const byGroup = GROUPS.map((g) => [g, chats.filter((c) => groupOf(c.updated_at) === g)]).filter(([, list]) => list.length);
  return (
    <aside className={`flex min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-slate-800 dark:bg-slate-900 ${className}`}>
      <div className="flex-none border-b border-gray-200 p-3 dark:border-slate-800">
        <button type="button" onClick={onNew}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700">
          <Plus className="h-4 w-4" />New chat
        </button>
      </div>
      <nav className="min-h-0 flex-1 overflow-y-auto p-2" aria-label="Team setup chats">
        {!chats.length && (
          <p className="px-2 py-4 text-center text-xs text-gray-500 dark:text-slate-400">Your chats are kept here, so nothing is lost if the tab closes.</p>
        )}
        {byGroup.map(([group, list]) => (
          <div key={group} className="mb-3">
            <div className="px-2 pb-1 text-xs font-medium text-gray-400 dark:text-slate-500">{group}</div>
            <ul className="space-y-0.5">
              {list.map((c) => {
                const on = c.id === currentId;
                return (
                  <li key={c.id} className="group relative">
                    <button type="button" onClick={() => onPick(c.id)} aria-current={on ? 'true' : undefined}
                      className={`flex w-full items-start gap-2 rounded-lg px-2 py-2 pr-8 text-left text-sm ${on
                        ? 'bg-blue-50 text-blue-900 dark:bg-blue-950/40 dark:text-blue-100'
                        : 'text-gray-700 hover:bg-gray-50 dark:text-slate-300 dark:hover:bg-slate-800/60'}`}>
                      {c.team_id
                        ? <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-600 dark:text-emerald-400" />
                        : <MessageSquare className="mt-0.5 h-4 w-4 flex-none text-gray-400 dark:text-slate-500" />}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{c.title}</span>
                        {c.team_id ? (
                          <span className="block text-xs text-emerald-700 dark:text-emerald-400">Team created</span>
                        ) : (
                          <span className="mt-1 flex items-center gap-2">
                            <span className="h-1 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-slate-800">
                              <span className="block h-full rounded-full bg-blue-500" style={{ width: `${c.progress}%` }} />
                            </span>
                            <span className="text-xs tabular-nums text-gray-500 dark:text-slate-400">{c.progress}%</span>
                          </span>
                        )}
                      </span>
                    </button>
                    <button type="button" onClick={() => onDelete(c)} title="Delete this chat"
                      className="absolute right-1.5 top-2 rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 focus:opacity-100 group-hover:opacity-100 dark:text-slate-500 dark:hover:bg-red-950/40 dark:hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default ChatHistory;
